/**
 * Paywall policy.
 *
 * This is a strategy document expressed as code. The rules in docs/08-monetization.md §4
 * live here rather than in each surface, so that "never during onboarding" and
 * "max 1 per session" are properties of the system rather than conventions three client
 * teams have to remember.
 *
 * Also note {@link LimitSheetProps}: its `alternatives` field is a non-empty tuple, so a
 * limit sheet physically cannot be constructed without offering the learner something
 * they can do for free. A strategic principle enforced by the type checker.
 */

import type { MeteredLimit } from './entitlements.js';

export type PaywallTrigger =
  | 'post_first_lesson'
  | 'voice_limit'
  | 'deep_analysis'
  | 'scenario_locked'
  | 'history_7d'
  | 'streak_7'
  | 'offline_request'
  | 'exam_track'
  | 'second_language'
  | 'winback'
  | 'custom_scenario_limit'
  /** Debate and Interview, which the tier table gives Plus and Pro and not free. */
  | 'advanced_modes';

export type PaywallVariant = 'primary' | 'comparison' | 'contextual' | 'winback';

export const TRIGGER_VARIANTS: Readonly<Record<PaywallTrigger, PaywallVariant>> = {
  post_first_lesson: 'primary',
  voice_limit: 'contextual',
  deep_analysis: 'contextual',
  scenario_locked: 'contextual',
  history_7d: 'contextual',
  streak_7: 'primary',
  offline_request: 'contextual',
  exam_track: 'comparison',
  second_language: 'comparison',
  winback: 'winback',
  custom_scenario_limit: 'contextual',
  advanced_modes: 'contextual',
};

export const MAX_PER_SESSION = 1;
export const MAX_PER_WEEK = 2;
export const DISMISS_COOLDOWN_MS = 60_000;

export interface PaywallGateInput {
  readonly trigger: PaywallTrigger;
  readonly now: number;
  /** True while the learner is still in the 9-step onboarding. */
  readonly inOnboarding: boolean;
  /** True while a lesson/scenario/review session is active. */
  readonly inActiveSession: boolean;
  /** True when this is the learner's very first app open. */
  readonly isFirstOpen: boolean;
  /** Already subscribed — no paywall, ever. */
  readonly isSubscribed: boolean;
  readonly shownThisSession: number;
  readonly shownThisWeek: number;
  readonly lastDismissedAt: number | null;
  /** Whether the learner has completed at least one lesson (proof of value). */
  readonly hasCompletedLesson: boolean;
}

export type PaywallBlockReason =
  | 'subscribed'
  | 'onboarding'
  | 'active_session'
  | 'first_open'
  | 'session_cap'
  | 'weekly_cap'
  | 'cooldown'
  | 'no_value_delivered';

export interface PaywallDecision {
  readonly show: boolean;
  readonly variant: PaywallVariant | null;
  readonly blockedBy: PaywallBlockReason | null;
}

/**
 * The winback trigger is exempt from the frequency caps because it fires inside a flow
 * the learner initiated (cancellation) and is shown exactly once by construction.
 */
const CAP_EXEMPT: readonly PaywallTrigger[] = ['winback'];

export function shouldShowPaywall(input: PaywallGateInput): PaywallDecision {
  const deny = (blockedBy: PaywallBlockReason): PaywallDecision => ({
    show: false,
    variant: null,
    blockedBy,
  });

  if (input.isSubscribed) return deny('subscribed');

  // Wedge W1: the learner must reach real value before any ask. Verbi's hard paywall at
  // 154 seconds, before a single spoken word, is the failure we are inverting.
  if (input.inOnboarding) return deny('onboarding');
  if (input.isFirstOpen) return deny('first_open');

  // Never interrupt practice. Limits surface after the current turn, not during it.
  if (input.inActiveSession) return deny('active_session');

  // `post_first_lesson` is meaningless without a completed lesson.
  if (input.trigger === 'post_first_lesson' && !input.hasCompletedLesson) {
    return deny('no_value_delivered');
  }

  if (!CAP_EXEMPT.includes(input.trigger)) {
    if (input.shownThisSession >= MAX_PER_SESSION) return deny('session_cap');
    if (input.shownThisWeek >= MAX_PER_WEEK) return deny('weekly_cap');
    if (input.lastDismissedAt !== null && input.now - input.lastDismissedAt < DISMISS_COOLDOWN_MS) {
      return deny('cooldown');
    }
  }

  return { show: true, variant: TRIGGER_VARIANTS[input.trigger], blockedBy: null };
}

// ── Limit sheets ──────────────────────────────────────────────────────────────

export type FreeAlternativeKind = 'text_mode' | 'review' | 'listen' | 'words' | 'grammar' | 'game';

export interface FreeAlternative {
  readonly kind: FreeAlternativeKind;
  readonly label: string;
  readonly estimatedMinutes: number;
  readonly route: string;
}

export interface UpgradeOffer {
  readonly skuId: string;
  readonly headline: string;
}

/**
 * `alternatives` is a non-empty tuple on purpose. Every limit the learner hits must come
 * with at least one thing they can do right now at no cost
 * (docs/03-design-system.md §6, docs/08-monetization.md §4).
 */
export interface LimitSheetProps {
  readonly limit: MeteredLimit;
  readonly resetsAt: number;
  readonly alternatives: readonly [FreeAlternative, ...FreeAlternative[]];
  readonly upgrade: UpgradeOffer;
}

export interface AlternativesContext {
  readonly dueCards: number;
  readonly freshListeningCount: number;
  readonly newWords: number;
  readonly canContinueInText: boolean;
}

/**
 * Builds the alternatives list, ordered by how useful they are *right now*. Returns at
 * least one entry in every case — the final fallback is the swipe deck, which is always
 * available offline and always free.
 */
export function buildAlternatives(
  ctx: AlternativesContext,
): readonly [FreeAlternative, ...FreeAlternative[]] {
  const list: FreeAlternative[] = [];

  if (ctx.canContinueInText) {
    list.push({
      kind: 'text_mode',
      label: 'Continue this conversation in text',
      estimatedMinutes: 4,
      route: '#text-mode',
    });
  }

  if (ctx.dueCards > 0) {
    list.push({
      kind: 'review',
      label: `Review ${ctx.dueCards} cards`,
      estimatedMinutes: Math.max(1, Math.round(ctx.dueCards * 0.25)),
      route: '/review/session',
    });
  }

  if (ctx.freshListeningCount > 0) {
    list.push({
      kind: 'listen',
      label:
        ctx.freshListeningCount === 1
          ? 'A new listening drop just landed'
          : `${ctx.freshListeningCount} new listening drops`,
      estimatedMinutes: 5,
      route: '/learn/listen',
    });
  }

  if (ctx.newWords > 0) {
    list.push({
      kind: 'words',
      label: `Learn ${Math.min(ctx.newWords, 10)} new words`,
      estimatedMinutes: 3,
      route: '/learn/words/deck',
    });
  }

  if (list.length === 0) {
    list.push({
      kind: 'game',
      label: 'Play a word game with your vocabulary',
      estimatedMinutes: 4,
      route: '/learn/words/games/wordsearch',
    });
  }

  // Safe: the branch above guarantees at least one element.
  return list as unknown as readonly [FreeAlternative, ...FreeAlternative[]];
}

// ── Trial disclosure ──────────────────────────────────────────────────────────

export interface TrialTimelineEntry {
  readonly at: number;
  readonly kind: 'start' | 'reminder_3d' | 'reminder_1d' | 'charge';
  readonly label: string;
}

/**
 * The dated schedule shown BEFORE the store sheet. 17–23% of Verbi's negative reviews
 * are subscription/refund complaints, and the verbatims point squarely at trial
 * ambiguity. Stating the exact date and amount up front is the cheapest defence there is.
 */
export function trialTimeline(
  startMs: number,
  trialDays: number,
  /**
   * The store's own localised price string, not a number of dollars.
   *
   * This took a `priceUsd` and printed `$79.99` at a learner who is billed in euros
   * by the App Store — a wrong amount on the one screen whose entire job is to state
   * the amount correctly. The store owns the price; we quote it.
   */
  priceLabel: string,
  formatDate: (ms: number) => string,
): readonly TrialTimelineEntry[] {
  const day = 86_400_000;
  const chargeAt = startMs + trialDays * day;
  const entries: TrialTimelineEntry[] = [
    { at: startMs, kind: 'start', label: 'Full access begins' },
  ];

  if (trialDays >= 5) {
    const at = chargeAt - 3 * day;
    entries.push({
      at,
      kind: 'reminder_3d',
      label: `${formatDate(at)} — we email and notify you`,
    });
  }

  const oneDay = chargeAt - day;
  entries.push({ at: oneDay, kind: 'reminder_1d', label: `${formatDate(oneDay)} — reminder` });
  entries.push({
    at: chargeAt,
    kind: 'charge',
    label: `${formatDate(chargeAt)} — ${priceLabel} charged unless you cancel`,
  });

  return entries;
}

/** The T−1d reminder is sent regardless of the toggle; the toggle only adds the T−3d touch. */
export function reminderSchedule(
  chargeAtMs: number,
  extraReminderOptedIn: boolean,
): readonly number[] {
  const day = 86_400_000;
  const schedule = [chargeAtMs - day];
  if (extraReminderOptedIn) schedule.unshift(chargeAtMs - 3 * day);
  return schedule.filter((t) => t > 0);
}
